import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import type { NotificationTemplate } from '../../lib/types';
import {
  Badge,
  Button,
  DataTable,
  FormField,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Textarea,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { MODULE_NOT_INCLUDED_MESSAGE, useClientPackage } from '../../lib/clientPackage';

const SAMPLE_VALUES: Record<string, string> = {
  buyer_name: 'Jane Smith',
  event_title: 'Winter Comedy Night',
  order_id: 'ord_01hq8z9k3f',
  ticket_count: '2',
  session_date: '12 Dec 2026, 7:30pm',
  venue_name: 'The Loctopus Theatre',
  total_amount: '$84.00',
};

function renderPreview(template: string, variables: string[] | null): string {
  let out = template;
  for (const v of variables ?? []) {
    const sample = SAMPLE_VALUES[v] ?? `{{${v}}}`;
    out = out.replaceAll(`{{${v}}}`, sample);
  }
  return out;
}

export function TemplatesPage() {
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const templatesQuery = useQuery({ queryKey: ['templates'], queryFn: api.templates.list });
  const { hasModule, isLoading: pkgLoading } = useClientPackage();
  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: this page serves BOTH `email` and `sms` channel
  // templates, so it's never fully gated the way PromoCodesPage.tsx etc. are (that would also
  // hide email template editing for a tenant without the `sms` module) - instead, only the SMS
  // rows/edit action are individually labeled when `sms` is off.
  const smsIncluded = pkgLoading || hasModule('sms');

  return (
    <div>
      <PageHeader
        title="Notification templates"
        description="Email/SMS templates for order and ticket events."
      />
      {templatesQuery.isLoading ? (
        <Spinner />
      ) : templatesQuery.error ? (
        <ErrorSummary error={toParsedError(templatesQuery.error)} />
      ) : (
        <DataTable<NotificationTemplate>
          columns={[
            { key: 'eventType', header: 'Event type', render: (t) => t.eventType },
            {
              key: 'channel',
              header: 'Channel',
              render: (t) =>
                t.channel === 'sms' && !smsIncluded ? (
                  <span
                    title={MODULE_NOT_INCLUDED_MESSAGE}
                    className="inline-flex items-center gap-1"
                  >
                    <Badge tone="neutral">sms</Badge>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-1 py-0.5">
                      Locked
                    </span>
                  </span>
                ) : (
                  <Badge>{t.channel}</Badge>
                ),
            },
            { key: 'subject', header: 'Subject', render: (t) => t.subject ?? '-' },
            {
              key: 'variables',
              header: 'Variables',
              render: (t) => t.variables?.join(', ') ?? '-',
            },
          ]}
          rows={templatesQuery.data?.data ?? []}
          getRowKey={(t) => t.id}
          onRowClick={(t) => {
            // SMS rows are still viewable (read-only preview) when `sms` is off - only editing is
            // blocked, consistent with the server independently enforcing the real gate on save.
            setEditing(t);
          }}
          emptyMessage="No notification templates yet."
        />
      )}
      {/* REAL BUG FOUND AND FIXED (same root cause as EventDetailPage.tsx's TicketTypeFormModal,
          found while live-verifying this round's clear-vs-untouched fix elsewhere - this page
          itself was "NOT LIVE-TESTABLE" per docs/FORM_SWEEP_RESULTS.md since no template rows
          were seeded, so this never got caught): this modal is always mounted, so
          `useState(template?.subject ?? '')`/`body` only ran once at this component's first
          mount (with `template=null`) - every click on a row re-rendered the SAME instance with
          the clicked template as a prop, but Subject/Body never picked it up, always showing
          blank instead of the real stored values. `key` forces a real remount per edited
          template, which also means `subject`/`body` genuinely reset to blank between two
          DIFFERENT templates rather than leaking one template's unsaved edits into the next. */}
      <TemplateEditModal
        key={editing?.id ?? 'none'}
        template={editing}
        onClose={() => setEditing(null)}
        smsIncluded={smsIncluded}
      />
    </div>
  );
}

function TemplateEditModal({
  template,
  onClose,
  smsIncluded,
}: {
  template: NotificationTemplate | null;
  onClose: () => void;
  smsIncluded: boolean;
}) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const preview = useMemo(
    () => renderPreview(body, template?.variables ?? null),
    [body, template?.variables],
  );

  const mutation = useMutation({
    mutationFn: () => api.templates.update(template!.id, { subject: subject || null, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  if (!template) return null;

  return (
    <Modal
      open={!!template}
      onClose={onClose}
      wide
      title={`${template.eventType} (${template.channel})`}
      footer={
        <RequirePermission permission="templates.manage">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {template.channel === 'sms' && !smsIncluded ? (
            <span className="text-sm text-slate-500" title={MODULE_NOT_INCLUDED_MESSAGE}>
              {MODULE_NOT_INCLUDED_MESSAGE}
            </span>
          ) : (
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          )}
        </RequirePermission>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <div className="grid grid-cols-2 gap-6">
        <div>
          {template.channel === 'email' && (
            <FormField label="Subject">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </FormField>
          )}
          <FormField
            label="Body"
            hint={`Available variables: ${(template.variables ?? []).map((v) => `{{${v}}}`).join(', ') || 'none'}`}
          >
            <Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} />
          </FormField>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Preview with sample data</p>
          <div className="border border-slate-200 rounded-brand p-4 bg-slate-50 whitespace-pre-wrap text-sm">
            {template.channel === 'email' && subject && (
              <p className="font-semibold mb-2">{renderPreview(subject, template.variables)}</p>
            )}
            {preview}
          </div>
        </div>
      </div>
    </Modal>
  );
}
